import { useState } from "react";
import { CalendarDays } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export type DateRangeOption =
  | "today"
  | "yesterday"
  | "last_7_days"
  | "last_14_days"
  | "last_21_days"
  | "last_30_days"
  | "this_month"
  | "last_month"
  | "all_time"
  | "custom";

/** The full preset list, shared by the table and the chart (same options). */
const DATE_RANGE_PRESETS: { value: DateRangeOption; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_14_days", label: "Last 14 Days" },
  { value: "last_21_days", label: "Last 21 Days" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "all_time", label: "All Time" },
  { value: "custom", label: "Custom Range" },
];

/**
 * A single custom-range date field. Native <input type="date"> shows the
 * browser-locale format (mm/dd/yyyy on en-US), which we don't want — this picker
 * always reads and writes dd.mm.yyyy. Value stays yyyy-mm-dd on the wire.
 */
function DateField({
  value,
  onChange,
  placeholder,
}: {
  value?: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;
  const shown = selected && isValid(selected) ? format(selected, "dd.MM.yyyy") : null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-9 items-center gap-2 rounded-md border border-border bg-transparent px-2 text-sm shadow-sm transition-colors hover:bg-muted/50 focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
        >
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <span className={cn(!shown && "text-muted-foreground")}>
            {shown ?? placeholder}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="single"
          selected={selected && isValid(selected) ? selected : undefined}
          onSelect={(date) => {
            if (date) {
              onChange(format(date, "yyyy-MM-dd"));
              setOpen(false);
            }
          }}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}

interface DateRangeFilterProps {
  dateRange: DateRangeOption;
  onDateRangeChange: (range: DateRangeOption) => void;
  compareEnabled: boolean;
  onCompareChange: (enabled: boolean) => void;
  customFrom?: string;
  customTo?: string;
  onCustomDateChange: (type: "from" | "to", date: string) => void;
  /** Presets to offer. Defaults to the table's list. */
  options?: { value: DateRangeOption; label: string }[];
  /** The chart reuses this control but has nothing to compare against. */
  showCompare?: boolean;
}

export function DateRangeFilter({
  dateRange,
  onDateRangeChange,
  compareEnabled,
  onCompareChange,
  customFrom,
  customTo,
  onCustomDateChange,
  options = DATE_RANGE_PRESETS,
  showCompare = true,
}: DateRangeFilterProps) {
  return (
    // Compact inline row so it sits at the same height as the other table
    // controls (Filters / Group By / Metrics), not a tall standalone card.
    <div className="flex flex-wrap items-center gap-3">
      <Select
        value={dateRange}
        onValueChange={(v) => onDateRangeChange(v as DateRangeOption)}
      >
        <SelectTrigger className="h-9 w-[170px] cursor-pointer border-border bg-muted/60">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <SelectValue placeholder="Select Period" />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {dateRange === "custom" && (
        <div className="flex items-center gap-2">
          <DateField
            value={customFrom}
            onChange={(v) => onCustomDateChange("from", v)}
            placeholder="From"
          />
          <span className="text-muted-foreground">–</span>
          <DateField
            value={customTo}
            onChange={(v) => onCustomDateChange("to", v)}
            placeholder="To"
          />
        </div>
      )}

      {showCompare && (
        <div className="flex items-center gap-2">
          <Switch
            id="compare-mode"
            checked={compareEnabled}
            onCheckedChange={onCompareChange}
          />
          <Label
            htmlFor="compare-mode"
            className="cursor-pointer whitespace-nowrap text-sm font-medium text-foreground"
          >
            Compare
          </Label>
        </div>
      )}
    </div>
  );
}
